import { getManufacturingOrderCodesForDisplay } from "../../../../utils/manufacturingOrderCode";

export interface ManufacturingScheduleItem {
    product_id?: string;
    product_name: string;
    product_category?: string;
    quantity: number;
    manufacturing_order_group_id?: string | null;
    manufacturing_order_code?: string | null;
    manufacturing_order_quantity?: number | null;
}

export interface ManufacturingScheduleOrder {
    id: string;
    due_date: string;
    order_name?: string;
    name?: string;
    customer_id?: string;
    customer_name: string;
    items: ManufacturingScheduleItem[];
}

export interface ManufacturingProductColumn {
    key: string;
    name: string;
    productId?: string;
}

export interface ManufacturingScheduleGroupRow {
    key: string;
    due_date: string;
    customerKey: string;
    customer_name: string;
    productKey: string;
    manufacturing_order_group_id?: string | null;
    manufacturing_order_code?: string | null;
    manufacturing_order_quantity?: number | null;
    manufacturing_order_group_ids: string[];
    manufacturing_order_codes: string[];
    customer_names: string[];
    orders: ManufacturingScheduleOrder[];
    productQuantities: Record<string, number>;
    orderProductQuantities: Record<string, Record<string, number>>;
}

const getProductKey = (item: ManufacturingScheduleItem) => item.product_id || item.product_name;
const getCustomerKey = (order: ManufacturingScheduleOrder) => order.customer_id || order.customer_name || "unknown";
const addUniqueValue = (values: string[], value?: string | null) => {
    const normalizedValue = (value || "").trim();
    if (normalizedValue && !values.includes(normalizedValue)) {
        values.push(normalizedValue);
    }
};

export const getOrderName = (order: ManufacturingScheduleOrder) => order.order_name ?? order.name ?? "—";

export const buildScheduleGroupRows = (
    orders: ManufacturingScheduleOrder[],
): ManufacturingScheduleGroupRow[] => {
    const rowsByGroup = new Map<string, ManufacturingScheduleGroupRow>();

    orders.forEach((order) => {
        const dueDate = order.due_date ?? "";
        const customerKey = getCustomerKey(order);

        order.items.forEach((item) => {
            const key = getProductKey(item);
            const rowKey = dueDate;

            if (!rowsByGroup.has(rowKey)) {
                rowsByGroup.set(rowKey, {
                    key: rowKey,
                    due_date: dueDate,
                    customerKey,
                    customer_name: order.customer_name || "—",
                    productKey: key,
                    manufacturing_order_group_id: item.manufacturing_order_group_id || null,
                    manufacturing_order_code: item.manufacturing_order_code || "",
                    manufacturing_order_quantity: item.manufacturing_order_quantity ?? null,
                    manufacturing_order_group_ids: [],
                    manufacturing_order_codes: [],
                    customer_names: [],
                    orders: [],
                    productQuantities: {},
                    orderProductQuantities: {},
                });
            }

            const row = rowsByGroup.get(rowKey)!;
            addUniqueValue(row.customer_names, order.customer_name || "—");
            addUniqueValue(row.manufacturing_order_group_ids, item.manufacturing_order_group_id);
            addUniqueValue(row.manufacturing_order_codes, item.manufacturing_order_code);
            if (!row.manufacturing_order_code && item.manufacturing_order_code) {
                row.manufacturing_order_group_id = item.manufacturing_order_group_id || null;
                row.manufacturing_order_code = item.manufacturing_order_code;
                row.manufacturing_order_quantity = item.manufacturing_order_quantity ?? null;
            }
            if (!row.orders.some((existingOrder) => existingOrder.id === order.id)) {
                row.orders.push(order);
            }
            if (!row.orderProductQuantities[order.id]) {
                row.orderProductQuantities[order.id] = {};
            }

            const quantity = Number(item.quantity || 0);
            row.productQuantities[key] = (row.productQuantities[key] || 0) + quantity;
            row.orderProductQuantities[order.id][key] = (
                row.orderProductQuantities[order.id][key] || 0
            ) + quantity;
        });
    });

    return Array.from(rowsByGroup.values()).sort((a, b) => (
        a.due_date.localeCompare(b.due_date)
    ));
};

export const buildProductColumns = (
    rows: ManufacturingScheduleGroupRow[],
): ManufacturingProductColumn[] => {
    const columnsByKey = new Map<string, ManufacturingProductColumn>();

    rows.forEach((row) => {
        row.orders.forEach((order) => {
            order.items.forEach((item) => {
                const key = getProductKey(item);
                if (!columnsByKey.has(key)) {
                    columnsByKey.set(key, {
                        key,
                        name: item.product_name,
                        productId: item.product_id,
                    });
                }
            });
        });
    });

    return Array.from(columnsByKey.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const buildScheduleExportRows = (
    rows: ManufacturingScheduleGroupRow[],
    productColumns: ManufacturingProductColumn[],
) => rows.map((row) => {
    const displayManufacturingOrderCodes = getManufacturingOrderCodesForDisplay(
        row.manufacturing_order_codes,
    );
    const exportRow: Record<string, string | number> = {
        due_date: row.due_date,
        manufacturing_order_count: displayManufacturingOrderCodes.length,
        manufacturing_order_codes: displayManufacturingOrderCodes.join(", "),
        customer_count: row.customer_names.length,
        customer_names: row.customer_names.join(", "),
        order_count: row.orders.length,
        order_names: row.orders.map(getOrderName).join(", "),
    };

    productColumns.forEach((product) => {
        exportRow[product.key] = row.productQuantities[product.key] || 0;
    });

    return exportRow;
});
